// src/components/domains/advisor-portal/dashboard/Schedule.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  format,
  isToday,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addDays,
  addWeeks,
  isSameDay,
} from "date-fns";
import { useEvents, EVENT_STATUS } from "../../../../contexts/EventContext";
import { Link } from "react-router-dom";
import EventModal from "../../../shared/calendar/EventModal";
import {
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { useDemo } from "../../../../contexts/DemoContext";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import api from "../../../../utils/apiConfig";
import { useTheme } from "../../../../contexts/ThemeContext";
import DashboardCard from "../../../shared/DashboardCard";

import logger from '../../../../utils/logger';

// Add time grid constants
// Modify time slots to end at 5 PM (10 slots)
const TIME_SLOTS = Array.from({ length: 10 }, (_, i) => i + 8); // 8 AM to 5 PM
const HOUR_HEIGHT = 22; // Compressed height
const TIME_SLOT_HEIGHT = `${HOUR_HEIGHT}px`;
const DAYS_TO_SHOW = 5; // Show 5 days instead of a week
const HEADER_HEIGHT = 10; // Reduced header height

const Schedule = () => {
  const {
    events,
    tasks,
    initializeEvents,
    addEvent,
    updateEvent,
    cleanEventTitle,
  } = useEvents();
  
  // Memoize state to prevent unnecessary re-renders
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [currentView, setCurrentView] = useState("weekly");
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Memoize demo-related state
  const { 
    isDemoMode, 
    selectedJourney, 
    startDemo, 
    demoStep,
    onAction
  } = useDemo();

  const navigate = useNavigate();
  const demoMeetingRef = useRef(null);
  
  // Memoize position states
  const [arrowPos, setArrowPos] = useState({ top: 0, left: 0 });
  const [descBoxPos, setDescBoxPos] = useState(null);
  const descBoxRef = useRef(null);
  
  // Memoize household names
  const [householdNames, setHouseholdNames] = useState({
    existing: "Smith",
    new: "Thompson",
  });
  
  const { isDarkMode } = useTheme();

  // Memoize derived demo states
  const isExistingClientDemo = useMemo(() => 
    isDemoMode && selectedJourney === 'Existing Client',
    [isDemoMode, selectedJourney]
  );
  
  const isNewClientDemo = useMemo(() => 
    isDemoMode && selectedJourney === 'New Client',
    [isDemoMode, selectedJourney]
  );

  // Memoize getViewEvents result
  const viewEvents = useMemo(() => {
    const allItems = [
      ...events,
      ...tasks.map((task) => ({
        ...task,
        isTask: true,
        backgroundColor: "#F3F4F6",
        textColor:
          task.status === EVENT_STATUS.COMPLETED ? "#059669" : "#374151",
      })),
    ];

    if (currentView === "daily") {
      return allItems.filter((event) => {
        const eDate = new Date(event.start);
        return (
          format(eDate, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
        );
      });
    }

    // Weekly - show events for the 5-day window starting from today
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    const periodEnd = addDays(today, DAYS_TO_SHOW - 1);
    periodEnd.setHours(23, 59, 59, 999); // End of the last day
    return allItems.filter((event) => {
      const eDate = new Date(event.start);
      return eDate >= today && eDate <= periodEnd;
    });
  }, [events, tasks, currentView, selectedDate]);

  // ─── Utility Functions ────────────────────────────────────────────────────────

  // 1) Build full list of events + tasks (so tasks show in the same "Schedule" view)
  const getViewEvents = () => {
    const allItems = [
      ...events,
      ...tasks.map((task) => ({
        ...task,
        isTask: true,
        backgroundColor: "#F3F4F6",
        textColor:
          task.status === EVENT_STATUS.COMPLETED ? "#059669" : "#374151",
      })),
    ];

    if (currentView === "daily") {
      return allItems.filter((event) => {
        const eDate = new Date(event.start);
        return (
          format(eDate, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
        );
      });
    }

    // Weekly - show events for the 5-day window starting from today
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    const periodEnd = addDays(today, DAYS_TO_SHOW - 1);
    periodEnd.setHours(23, 59, 59, 999); // End of the last day
    return allItems.filter((event) => {
      const eDate = new Date(event.start);
      return eDate >= today && eDate <= periodEnd;
    });
  };

  // 2) Group by "YYYY-MM-DD"
  const groupedEvents = useMemo(() => {
    const viewEvents = getViewEvents();
    return viewEvents.reduce((groups, event) => {
      const d = new Date(event.start);
      const dateStr = format(d, "yyyy-MM-dd");
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(event);
      return groups;
    }, {});
  }, [events, tasks, currentView]); // Removed selectedDate dependency since we always use today

  // 3) Ordinal suffix helper
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // 4) Format a date (e.g. "Monday, June 2nd, 2025")
  const formatDateWithOrdinal = (date) => {
    const d = parseInt(format(date, "d"));
    const suffix = getOrdinalSuffix(d);
    return format(date, `EEEE, MMMM d'${suffix}', yyyy`);
  };

  // 5) Format a full week range with ordinals
  const formatDateRangeWithOrdinal = (startDate) => {
    const today = new Date();
    const endDate = addDays(today, DAYS_TO_SHOW - 1);
    const sd = parseInt(format(today, "d"));
    const ed = parseInt(format(endDate, "d"));
    const sfx = getOrdinalSuffix(sd);
    const efx = getOrdinalSuffix(ed);
    return `${format(today, `EEEE, MMMM d'${sfx}'`)} - ${format(endDate, `EEEE, MMMM d'${efx}', yyyy`)}`;
  };

  // 6) Return an array of the 7 weekdays around selectedDate
  const getWeekDays = () => {
    const today = new Date();
    return Array.from({ length: DAYS_TO_SHOW }, (_, index) => 
      addDays(today, index)
    );
  };

  // 7) Format a time range string "2:00 PM – 3:00 PM"
  const formatTime = (date) => format(new Date(date), "h:mm a");
  const formatTimeRange = (start, end) =>
    `${formatTime(start)} - ${formatTime(end)}`;

  // 8) Sort events by start time
  const sortedEvents = getViewEvents().sort(
    (a, b) => new Date(a.start) - new Date(b.start),
  );

  // 9) Demo-mode helpers
  const nonTaskMeetings = useMemo(
    () => sortedEvents.filter((e) => {
      if (e.isTask) return false;
      // In weekly view, only consider today's meetings for demo
      if (currentView === "weekly") {
        return isToday(new Date(e.start));
      }
      return true;
    }),
    [sortedEvents, currentView]
  );
  const firstMeeting = nonTaskMeetings[0];
  const secondMeeting = nonTaskMeetings[1];

  // Add time formatting helper
  const formatHour = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  // Calculate event position and height
  const getEventStyle = (event) => {
    const start = new Date(event.start);
    const end   = new Date(event.end);

    const startMins = (start.getHours() - 8) * 60 + start.getMinutes();
    const endMins   = (end  .getHours() - 8) * 60 + end  .getMinutes();

    // each hour = HOUR_HEIGHT px → each minute = HOUR_HEIGHT/60 px
    const topPx    = Math.round((startMins / 60) * HOUR_HEIGHT);
    const heightPx = Math.max(Math.round(((endMins - startMins) / 60) * HOUR_HEIGHT), 20);

    return {
      position: "absolute",
      top:    `${topPx}px`,
      height: `${heightPx}px`,
      width:  "90%",
      left:   "5%",
      zIndex: 1,  // Lower z-index to stay beneath modal
    };
  };

  // ─── Lifecycle & Effects ─────────────────────────────────────────────────────

  // Position demo arrow / prompt around the card
  useEffect(() => {
    if (
      (isExistingClientDemo || isNewClientDemo) &&
      demoStep === 0 &&
      demoMeetingRef.current
    ) {
      // Ensure the demo card is visible
      demoMeetingRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [isExistingClientDemo, isNewClientDemo, demoStep]);

  // Fetch household names in demo mode
  useEffect(() => {
    const fetchHouseholdNames = async () => {
      if (!isDemoMode) return;
      
      try {
        const [existingResponse, newResponse] = await Promise.all([
          api.get("/advisor-portal/client-database/household-profile/253"),
          api.get("/advisor-portal/client-database/household-profile/283"),
        ]);

        setHouseholdNames({
          existing: existingResponse.data.members?.[0]?.lastName || "Smith",
          new: newResponse.data.members?.[0]?.lastName || "Thompson",
        });
      } catch (err) {
        logger.error("Error fetching household names:", err);
      }
    };

    fetchHouseholdNames();
  }, [isDemoMode]);

  // Initialize the events context on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      initializeEvents();
    }
  }, []); // Empty dependency array

  // Debugger for events/tasks
  useEffect(() => {
    logger.info("Events/tasks updated:", {
      eventCount: events.length,
      taskCount: tasks.length,
    });
  }, [events, tasks]);

  // ─── Event Handlers ────────────────────────────────────────────────────────────

  const handleEventClick = (event) => {
    const eventToEdit = {
      id: event.id,
      title: event.title || "",
      start: new Date(event.start),
      end: new Date(event.end),
      allDay: !!event.allDay,
      type: event.type || "CLIENT",
      backgroundColor: event.backgroundColor,
      borderColor: event.borderColor,
      extendedProps: {
        ...event.extendedProps,
        attendees: event.extendedProps?.attendees || [],
        virtualMeetingLink: event.extendedProps?.virtualMeetingLink || "",
        status: event.extendedProps?.status || EVENT_STATUS.SCHEDULED,
        location: event.extendedProps?.location || "",
        description: event.extendedProps?.description || "",
      },
    };

    setSelectedEvent(eventToEdit);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleCreateEvent = () => {
    const now = new Date();
    const roundedMinutes = Math.floor(now.getMinutes() / 60) * 60;
    const roundedStart = new Date(now.setMinutes(roundedMinutes, 0, 0));
    const roundedEnd = new Date(roundedStart.getTime() + 60 * 60 * 1000);

    const newEvent = {
      id: "",
      title: "",
      start: roundedStart,
      end: roundedEnd,
      allDay: false,
      type: "CLIENT",
      extendedProps: {
        attendees: [],
        virtualMeetingLink: "",
        status: EVENT_STATUS.SCHEDULED,
        location: "",
        description: "",
      },
    };

    setSelectedEvent(newEvent);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleModalSubmit = (eventData) => {
    if (modalMode === "create") {
      addEvent(eventData);
    } else {
      updateEvent(eventData);
    }
    setIsModalOpen(false);
  };

  const handleTodayClick = () => {
    setCurrentView("daily");
    setSelectedDate(new Date());
  };
  const handleWeeklyClick = () => {
    setCurrentView("weekly");
  };
  const handlePrevious = () => {
    if (currentView === "daily") {
      setSelectedDate((prev) => addDays(prev, -1));
    } else {
      setSelectedDate((prev) => addDays(prev, -DAYS_TO_SHOW));
    }
  };
  const handleNext = () => {
    if (currentView === "daily") {
      setSelectedDate((prev) => addDays(prev, 1));
    } else {
      setSelectedDate((prev) => addDays(prev, DAYS_TO_SHOW));
    }
  };

  // ─── EventCard Subcomponent (with demo spotlight logic) ───────────────────────

  const EventCard = ({ event, isWeekly = false, isDemoCard = false, style = {} }) => {
    const isEventToday = isToday(new Date(event.start));
    const isClickable = isDemoCard || (!isExistingClientDemo && !isNewClientDemo);
    const shouldShowDemoStyling = isDemoCard && (!isWeekly || (isWeekly && isEventToday));

      const handleDemoClick = () => {
    if (isWeekly && !isEventToday) return;

    if (isDemoMode && selectedJourney === 'Existing Client' && demoStep === 0) {
      // Mark the first meeting as completed when demo advances
      if (event.id === 'demo-annual-review') {
        updateEvent({
          ...event,
          extendedProps: {
            ...event.extendedProps,
            status: EVENT_STATUS.COMPLETED,
          },
        });
      }
      onAction({ type: 'ADVANCE_DEMO' });
      navigate('/advisor-portal/client-database/household-profile/253/ar-profile/2025/1', {
        state: { 
          startDemo: true,
          demoStep: 1,
          selectedJourney: 'Existing Client'
        }
      });
      return;
    }

    if (!isDemoMode && isDemoCard) {
      // Don't call startDemo - just navigate to the Annual Review Profile
      // The demo will start naturally through the CallConfirmationModal flow
      navigate('/advisor-portal/client-database/household-profile/253/ar-profile/2025/1', {
        state: { startDemo: true }
      });
      return;
    }

    if (!isExistingClientDemo && !isNewClientDemo) {
      handleEventClick(event);
    }
  };

    const cardStyle = {
      pointerEvents: isClickable && (!isWeekly || (isWeekly && isEventToday)) ? "auto" : "none",
      zIndex: shouldShowDemoStyling && isExistingClientDemo && demoStep === 0 ? 10 : 1,
      opacity: (isExistingClientDemo || isNewClientDemo) && !shouldShowDemoStyling ? 0.5 : 1,
      background: isDarkMode 
        ? shouldShowDemoStyling ? "rgba(35, 39, 58, 0.98)" : "rgba(13, 17, 23, 0.8)"
        : event.isTask ? "#F3F4F6" : "#fff",
      transition: "all 0.2s ease-in-out",
      borderRadius: "4px",
      ...((!event.isTask) && {
        borderLeft: `2px solid ${event.backgroundColor || event.color || "#0D9488"}`,
      }),
      ...(isWeekly && shouldShowDemoStyling && {
        boxShadow: isDarkMode 
          ? "0 0 0 1px rgba(45, 212, 191, 0.3)"
          : "0 0 0 1px rgba(13, 148, 136, 0.3)",
        transform: "scale(1.01)",
      }),
      ...style,
    };

    return (
      <div
        id={shouldShowDemoStyling && isExistingClientDemo && demoStep === 0 ? "annual-review-demo-meeting" : undefined}
        ref={
          (isExistingClientDemo || isNewClientDemo) &&
          shouldShowDemoStyling &&
          demoStep === 0
            ? demoMeetingRef
            : null
        }
        data-allow-demo-click={shouldShowDemoStyling ? "true" : undefined}
        onClick={isClickable && (!isWeekly || (isWeekly && isEventToday)) ? handleDemoClick : undefined}
        style={cardStyle}
        className={`
          relative box-border transition-all duration-200
          hover:shadow-md hover:bg-opacity-90
          ${isDarkMode ? "hover:bg-teal-900/30" : "hover:bg-teal-50"}
          ${isClickable
            ? "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
            : "opacity-50 cursor-not-allowed"}
        `}
      >
        <div className="flex items-start p-1">
          <div className="flex-grow min-w-0">
            <div className="flex items-start justify-between gap-1">
              <div className="flex items-center gap-1 max-w-full">
                {(event.isTask || event.extendedProps?.status === EVENT_STATUS.COMPLETED) ? (
                  event.extendedProps?.status === EVENT_STATUS.COMPLETED ? (
                    <CheckCircleIcon className="w-3 h-3 flex-shrink-0 text-teal-600 dark:text-teal-400" />
                  ) : (
                    <ClockIcon className="w-3 h-3 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                  )
                ) : null}
                <p
                  className={`text-[9px] font-medium line-clamp-1 transition-colors
                    ${event.isTask || event.extendedProps?.status === EVENT_STATUS.COMPLETED
                      ? event.extendedProps?.status === EVENT_STATUS.COMPLETED
                        ? "text-teal-600 dark:text-teal-400 line-through"
                        : "text-gray-700 dark:text-gray-300"
                      : "text-gray-900 dark:text-gray-100"
                    } 
                    ${shouldShowDemoStyling ? "font-semibold" : ""}
                    group-hover:text-teal-600 dark:group-hover:text-teal-400
                  `}
                >
                  {cleanEventTitle(event.title)}
                </p>
              </div>
              {!event.isTask && event.extendedProps?.attendees?.length > 0 && (
                <div className="flex items-center text-[8px] text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                  <UserGroupIcon className="w-2 h-2 mr-0.5" />
                  {event.extendedProps.attendees.length}
                </div>
              )}
            </div>
            {!event.isTask && (
              <p className="text-[8px] text-gray-500 dark:text-gray-400 mt-0.5">
                {formatTimeRange(event.start, event.end)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────────

  const dateNavigation = (
    <div className="flex items-center justify-center">
      <button
        onClick={handlePrevious}
        className={`p-1 rounded-full ${
          isDarkMode
            ? "hover:bg-gray-700 text-gray-300 hover:text-gray-100"
            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
        }`}
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      <span
        className={`${isDarkMode ? "text-teal-300" : "text-blue-700"} text-xs font-medium mx-3`}
      >
        {currentView === "daily"
          ? formatDateWithOrdinal(selectedDate)
          : formatDateRangeWithOrdinal(selectedDate)}
      </span>
      <button
        onClick={handleNext}
        className={`p-1 rounded-full ${
          isDarkMode
            ? "hover:bg-gray-700 text-gray-300 hover:text-gray-100"
            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
        }`}
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );

  // Update the header controls to be inline
  const headerControls = (
    <div className="flex items-center justify-between px-4 mb-1">
      {/* Left arrows + date */}
      <div className="flex-1 flex justify-center items-center whitespace-nowrap">
        <button
          onClick={handlePrevious}
          className={`w-5 h-5 cursor-pointer transition-colors duration-200 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300`}
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 mx-3">{
          currentView === "daily"
            ? formatDateWithOrdinal(selectedDate)
            : formatDateRangeWithOrdinal(selectedDate)
        }</span>
        <button
          onClick={handleNext}
          className={`w-5 h-5 cursor-pointer transition-colors duration-200 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300`}
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Right buttons */}
      <div className="flex items-center space-x-2 whitespace-nowrap">
                  <button
            onClick={handleTodayClick}
            className={`px-3 py-1 text-xs font-medium ${
              currentView === "daily" && isSameDay(selectedDate, new Date())
                ? isDarkMode
                  ? "bg-gradient-to-br from-aurora-start via-aurora-mid to-aurora-end text-white"
                  : "bg-blue-600 text-white"
                : isDarkMode
                  ? "bg-teal-600 text-white"
                  : "bg-blue-50 text-blue-600 border border-blue-200"
            } rounded hover:opacity-90 transition-opacity duration-150`}
        >
          Today
        </button>
                  <button
            onClick={handleWeeklyClick}
            className={`px-3 py-1 text-xs font-medium ${
              currentView === "weekly"
                ? isDarkMode
                  ? "bg-gradient-to-br from-aurora-start via-aurora-mid to-aurora-end text-white"
                  : "bg-blue-600 text-white"
                : isDarkMode
                  ? "bg-teal-600 text-white"
                  : "bg-blue-50 text-blue-600 border border-blue-200"
            } rounded hover:opacity-90 transition-opacity duration-150`}
        >
          Weekly
        </button>
                  <button
            onClick={handleCreateEvent}
            className={`px-3 py-1 text-xs font-medium ${
              isDarkMode
                ? "bg-gradient-to-br from-aurora-start via-aurora-mid to-aurora-end text-white"
                : "bg-blue-600 text-white"
            } rounded hover:opacity-90 transition-opacity duration-150`}
        >
          Create Event
        </button>

      </div>
    </div>
  );

  return (
    <DashboardCard
      title="Upcoming Schedule & Tasks"
      icon={CalendarIcon}
      headerControls={headerControls}
    >
      <div className="flex flex-col h-full">
        <div className="relative p-2">
          {currentView === "daily" ? (
            <div className="space-y-2">
              <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {Object.entries(groupedEvents).map(([dateStr, dayEvents]) =>
                  dayEvents.map((event) => {
                    const isDemoCard = isExistingClientDemo
                      ? event.id === firstMeeting?.id
                      : isNewClientDemo
                        ? event.id === secondMeeting?.id
                        : false;

                    return (
                      <div key={event.id} className="min-w-[250px] max-w-[250px]">
                        <EventCard event={event} isDemoCard={isDemoCard} />
                      </div>
                    );
                  }),
                )}
                {Object.keys(groupedEvents).length === 0 && (
                  <div className="flex items-center justify-center w-full py-4">
                    <p className="text-xs text-gray-500 dark:text-gray-500 dark:text-gray-400">
                      No events scheduled for this day
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              {/* Day labels row */}
              <div className="grid grid-cols-5 text-[10px] font-medium mb-1">
                {getWeekDays().map(day => {
                  const isCurrentDay = isToday(day);
                  return (
                    <div 
                      key={format(day, "yyyy-MM-dd")} 
                      className={`text-center ${
                        isCurrentDay 
                          ? "text-teal-600 dark:text-teal-400" 
                          : isDarkMode 
                            ? "text-gray-200" 
                            : "text-gray-500"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <span>{format(day, "EEE")}</span>
                        <span>{format(day, "d")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Slot grid with fixed height */}
              <div className="flex overflow-y-auto" style={{ maxHeight: TIME_SLOTS.length * HOUR_HEIGHT }}>
                {/* Time column */}
                <div className="w-12 flex-shrink-0 pr-1">
                  {TIME_SLOTS.map(hour => (
                    <div 
                      key={hour} 
                      className="text-[9px] text-gray-500 dark:text-gray-400 flex-shrink-0" 
                      style={{ height: TIME_SLOT_HEIGHT }}
                    >
                      {formatHour(hour)}
                    </div>
                  ))}
                </div>

                {/* Events grid */}
                <div className="flex-1 grid grid-cols-5 gap-1" style={{ gridAutoRows: TIME_SLOT_HEIGHT }}>
                  {getWeekDays().map(day => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const dayEvents = groupedEvents[dateStr] || [];
                    const isCurrentDay = isToday(day);

                    return (
                      <div
                        key={dateStr}
                        className={`
                          relative
                          ${
                            isCurrentDay
                              ? `${isDarkMode ? "bg-teal-900/20" : "bg-teal-50"}`
                              : `${isDarkMode ? "bg-gray-800/30" : "bg-gray-50"}`
                          }
                        `}
                        style={{ height: `${TIME_SLOTS.length * HOUR_HEIGHT}px` }}
                      >
                        {/* Time grid lines */}
                        {TIME_SLOTS.map((hour) => (
                          <div
                            key={hour}
                            className="border-t border-gray-200 dark:border-gray-700/50"
                            style={{ height: TIME_SLOT_HEIGHT }}
                          />
                        ))}

                        {/* Events */}
                        {dayEvents.map((event) => {
                          const isDemoCard = isExistingClientDemo
                            ? event.id === firstMeeting?.id
                            : isNewClientDemo
                              ? event.id === secondMeeting?.id
                              : false;

                          const style = {
                            ...getEventStyle(event),
                            boxSizing: "border-box",
                            overflow: "hidden",
                          };

                          return (
                            <EventCard
                              key={event.id}
                              event={event}
                              isWeekly={true}
                              isDemoCard={isDemoCard}
                              style={style}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with View Full Calendar link */}
        <div className="mt-auto py-1.5 pb-1 px-3 flex justify-end">
          <Link
            to="/advisor-portal/calendar-events"
            state={{
              view: currentView === "daily" ? "timeGridDay" : "timeGridWeek",
            }}
            className="text-xs text-blue-600 dark:text-teal-300 hover:underline"
          >
            View Full Calendar
          </Link>
        </div>

        {/* Event Modal */}
        <EventModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedEvent(null);
          }}
          onSubmit={handleModalSubmit}
          initialData={selectedEvent}
          mode={modalMode}
        />
      </div>
    </DashboardCard>
  );
};

export default Schedule;
